import { IntlShape } from "react-intl";
import { ErrorRecord, IFormField, ITextField, IUIItem, logger, stringUtils } from "cayo.ui";
import { Entity } from "@cayo/shared/api/types/cayo-graph";
import { commonMessages } from "@cayo/shared/messages/common";
import { objectUtils } from "@cayo/shared/utils/object";
import { checkableTypes, errorCheckers } from "./error-checkers";
import modelUtils from "./modelUtils";
import { IObjectPicker } from "../../api/schema.api";
import { regexs } from "../../utils/regexs";

type Field = string | { value?: any };

function extractValue(field: Field | undefined): string {
  if (typeof field === "string") {
    return field;
  }

  if (field && typeof field === "object" && "value" in field) {
    const value = field.value;
    return value !== undefined && value !== null ? String(value) : "";
  }

  return "";
}

const checkError = (
  annotation: IFormField,
  v: any,
  setError: (name: string, error?: ErrorRecord) => void,
  intl: IntlShape
) => {
  let errorMessage: string | undefined = undefined;
  let index: number | string | undefined = undefined;
  // TODO: check value for choice group
  const hasValue = objectUtils.hasValue(v, annotation.required);
  const isPicker = (annotation as IObjectPicker).isPicker;
  const isMultiValue = Array.isArray(v) && !isPicker;

  if (!v && !annotation.required) {
    setError(annotation.name!, undefined);
    return false;
  }

  const checkerName = stringUtils.dashedToLowCase(annotation.type);
  const errorChecker = errorCheckers[checkerName as checkableTypes];
  if (errorChecker) {
    const { hasError, error } = errorChecker(annotation, v);
    if (error) {
      setError(annotation.name!, { errorMessage: error });
    }

    return hasError;
  }

  if (hasValue && (typeof v === "string" || typeof v === "number")) {
    const value = v?.toString();
    const textAnnotation = annotation as ITextField;
    if (textAnnotation.pattern && !value.match(textAnnotation.pattern)) {
      errorMessage =
        textAnnotation.errors?.pattern || intl.formatMessage(commonMessages.matchFormat);
      const eg = annotation.placeHolder?.match(regexs.afterEG);
      if (eg) {
        errorMessage += " " + eg[0];
      }
    }

    const { minChars, maxChars } = textAnnotation;
    if (minChars && maxChars && !textAnnotation.errors?.pattern) {
      if (value.length > maxChars || value.length < minChars) {
        errorMessage = intl.formatMessage(commonMessages.enterBetweenChars, { minChars, maxChars });
      }
    }
    if (minChars && !maxChars) {
      if (value.length < minChars) {
        errorMessage = intl.formatMessage(commonMessages.enterAtLeastChars, { minChars });
      }
    }
    if (maxChars && !minChars) {
      if (value.length > maxChars) {
        errorMessage = intl.formatMessage(commonMessages.enterNoMoreThanChars, { maxChars });
      }
    }
  }

  if (!hasValue && annotation.required) {
    errorMessage = annotation.errors?.required || intl.formatMessage(commonMessages.inputRequired);
  } else if (isMultiValue) {
    const textAnnotation = annotation as ITextField;
    v.forEach((field, idx) => {
      const value = extractValue(field);
      if (annotation.required && !value) {
        errorMessage = intl.formatMessage(commonMessages.inputRequired);
        index = idx;
        return;
      }
      if (textAnnotation.pattern && !value.match(textAnnotation.pattern)) {
        errorMessage = intl.formatMessage(commonMessages.matchFormat);
        const eg = annotation.placeHolder?.match(regexs.afterEG);
        if (eg) {
          errorMessage += " " + eg[0];
        }
        index = idx;
        return;
      }
    });
  }

  const error = errorMessage ? { index, errorMessage } : undefined;

  if (error) {
    logger.debug(`Error found. Control: ${annotation.name}, msg: ${error}`);
  }

  setError(annotation.name!, error);

  return !!errorMessage;
};

type FormItem = IUIItem & { modelProperty?: string };

const getName = (item: FormItem) => {
  return item.modelProperty || item.name;
};

const isComplexItem = (item: FormItem) => {
  return !!item.modelProperty;
};

const isPasswordSet = (annotation: ITextField, object?: Entity) => {
  const isPasswordSet =
    object &&
    modelUtils.resolvePropertyValue(`${formUtils.getName(annotation)}@confidential`, object) ===
      "present";

  return isPasswordSet;
};

export const formUtils = {
  checkError,
  getName,
  isComplexItem,
  isPasswordSet,
};
